import { useCallback, useMemo, useRef } from "react";

import { useQuery } from "@apollo/client";

import { RETRY_TRANSIENT } from "../../apollo/provider/createTransientRetryLink";
import {
    type PayoutSortBy,
    type PayoutSortOrder,
    type PayoutStatsInput,
    type PayoutStatsResponse,
    type RewardedLens,
} from "../../gql-generated/types";
import { getPayoutStatsLensList } from "../api/queries/getPayoutStatsLensList";

interface UsePayoutStatsLensListParams {
    startDate?: string;
    endDate?: string;
    countryFilters: string[];
    sortBy: PayoutSortBy;
    sortOrder: PayoutSortOrder;
    limit: number;
    skip?: boolean;
}

interface UsePayoutStatsLensListResult {
    loading: boolean;
    error?: Error;
    lenses: RewardedLens[];
    totalLensCount: number;
    hasMore: boolean;
    fetchMore: () => void;
}

export const usePayoutStatsLensList = ({
    startDate,
    endDate,
    countryFilters,
    sortBy,
    sortOrder,
    limit,
    skip,
}: UsePayoutStatsLensListParams): UsePayoutStatsLensListResult => {
    const input: PayoutStatsInput = {
        startDate,
        endDate,
        countryFilters,
        lensList: {
            sortBy,
            sortOrder,
            limit,
        },
    };

    const fetchingMoreRef = useRef(false);

    const {
        data,
        loading,
        error,
        fetchMore: apolloFetchMore,
    } = useQuery<{ payoutStats: PayoutStatsResponse }>(getPayoutStatsLensList, {
        variables: { input },
        skip,
        context: RETRY_TRANSIENT,
    });

    const lensList = data?.payoutStats.lensList;
    const cursor = lensList?.cursor;
    const hasMore = Boolean(cursor);

    const lenses = useMemo(() => [...(lensList?.lenses ?? [])], [lensList?.lenses]);
    const totalLensCount = lensList?.totalLensCount ?? 0;

    const fetchMore = useCallback(() => {
        if (!cursor || fetchingMoreRef.current) return;
        fetchingMoreRef.current = true;

        void apolloFetchMore({
            variables: {
                input: {
                    startDate,
                    endDate,
                    countryFilters,
                    lensList: {
                        sortBy,
                        sortOrder,
                        limit,
                        cursor,
                    },
                },
            },
        }).finally(() => {
            fetchingMoreRef.current = false;
        });
    }, [apolloFetchMore, cursor, startDate, endDate, countryFilters, sortBy, sortOrder, limit]);

    return {
        loading,
        error,
        lenses,
        totalLensCount,
        hasMore,
        fetchMore,
    };
};
